import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'

import { AmountInput } from '@/components/AmountInput'
import { feeAmountText, useFeeLabel } from '@/components/CpmFeeBreakdown'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import {
  calculateNetCpm,
  resolveFeeCurrency,
  type DisplayFee,
  type Fee,
} from '@/services/earnings/fees'
import type { CurrencyAmount } from '@/services/lineItems/lineItemsApi'
import { formatCurrencyAmount, formatMoneyRange } from '@/utils/money'

/** Which of the line item's two lists a fee belongs to. */
type FeeKind = 'CPM' | 'SUPPLEMENTARY'
/** A fee is charged as a fixed amount or as a share of the gross CPM. */
type FeeUnit = 'MONETARY' | 'PERCENTAGE'

interface DraftFee {
  /** Stable across adds and removes, so a row keeps its inputs. */
  key: number
  kind: FeeKind
  name: string
  unit: FeeUnit
  /** As typed, so a half-typed value survives. */
  amount: string
  /** Set by the server on a fee that came from the team; kept through edits. */
  reference?: string
  /** The fee as loaded, sent back as-is for as long as the row is not edited. */
  untouched?: Fee
  /** Added in this sitting — takes the cursor when it appears. */
  added?: boolean
}

export interface CpmFeesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The line item's own fees. */
  cpmFees: Fee[]
  /** Fees ad ops add on top — what the network may call a "team custom fee". */
  supplementaryFees: Fee[]
  /** Fees the platform derives — tech, audience, creative format — shown read-only. */
  derivedFees: DisplayFee[]
  grossCpm?: CurrencyAmount
  /** The line item's currency, which every fixed fee is charged in. */
  currency: string
  /** Only ad ops may change the supplementary fees; to anyone else they are read-only. */
  canEditSupplementary: boolean
  /** Whether new supplementary fees can be added — marketplace networks only. */
  canAddSupplementary: boolean
  /** What the network calls a supplementary fee. */
  supplementaryLabel: string
  onSave: (fees: { cpmFees: Fee[]; supplementaryFees: Fee[] }) => void
}

function draftOf(fee: Fee, kind: FeeKind, currency: string, key: number): DraftFee {
  const unit: FeeUnit = fee.type === 'PERCENTAGE' ? 'PERCENTAGE' : 'MONETARY'
  const amount =
    unit === 'PERCENTAGE' ? fee.percentage : resolveFeeCurrency(fee, currency).monetary?.amount
  return {
    key,
    kind,
    name: fee.name ?? '',
    unit,
    amount: amount?.toString() ?? '',
    reference: fee.reference,
    untouched: fee,
  }
}

/** The fee a row stands for, or undefined while its amount is not a number. */
function feeOf(row: DraftFee, currency: string): Fee | undefined {
  if (row.untouched) return row.untouched
  const amount = parseFloat(row.amount)
  if (isNaN(amount)) return undefined
  return {
    type: row.unit,
    name: row.name.trim(),
    ...(row.reference ? { reference: row.reference } : {}),
    ...(row.unit === 'PERCENTAGE' ? { percentage: amount } : { monetary: { amount, currency } }),
  }
}

/**
 * Edits the fees charged against a line item's gross CPM: its own CPM fees, and
 * — for ad ops — the supplementary fees. The fees the platform derives are
 * listed read-only beneath, and the net CPM they all leave is worked out as the
 * fees are typed.
 *
 * Applying hands the fees back to the form; the line item's own save sends them.
 */
export function CpmFeesDialog({
  open,
  onOpenChange,
  cpmFees,
  supplementaryFees,
  derivedFees,
  grossCpm,
  currency,
  canEditSupplementary,
  canAddSupplementary,
  supplementaryLabel,
  onSave,
}: CpmFeesDialogProps) {
  const { t } = useTranslation()
  const feeLabel = useFeeLabel()
  const [draft, setDraft] = useState<DraftFee[]>([])
  const nextKey = useRef(0)

  // Opening is what seeds the editor, so a cancelled edit leaves nothing behind.
  useEffect(() => {
    if (!open) return
    const rows = [
      ...cpmFees.map((fee) => draftOf(fee, 'CPM', currency, nextKey.current++)),
      ...(canEditSupplementary
        ? supplementaryFees.map((fee) =>
            draftOf(fee, 'SUPPLEMENTARY', currency, nextKey.current++)
          )
        : []),
    ]
    setDraft(rows)
    // Seeded once per opening: later prop churn must not throw away edits.
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const update = (key: number, changes: Partial<DraftFee>) =>
    setDraft((rows) =>
      rows.map((row) => (row.key === key ? { ...row, ...changes, untouched: undefined } : row))
    )

  const remove = (key: number) => setDraft((rows) => rows.filter((row) => row.key !== key))

  // New fees go on top, where they are seen.
  const add = (kind: FeeKind) =>
    setDraft((rows) => [
      { key: nextKey.current++, kind, name: '', unit: 'MONETARY', amount: '', added: true },
      ...rows,
    ])

  // Supplementary fees an ad ops user cannot change still count towards the net.
  const readOnlySupplementary: DisplayFee[] = canEditSupplementary
    ? []
    : supplementaryFees.map((fee) => ({
        ...resolveFeeCurrency(fee, currency),
        source: 'SUPPLEMENTARY',
      }))
  const readOnlyFees = [...readOnlySupplementary, ...derivedFees]

  const draftFees: DisplayFee[] = draft.flatMap((row) => {
    const fee = feeOf(row, currency)
    return fee ? [{ ...resolveFeeCurrency(fee, currency), source: row.kind }] : []
  })
  const netCpm = calculateNetCpm([...draftFees, ...readOnlyFees], grossCpm)
  const feesExceedGross = !!netCpm && netCpm.amount < 0

  // Only what was typed here is checked: a fee loaded as it is goes back as it is.
  const edited = draft.filter((row) => !row.untouched)
  const blockedBy = edited.some((row) => !row.name.trim())
    ? 'nameRequired'
    : edited.some((row) => !(parseFloat(row.amount) >= 0))
      ? 'amountRequired'
      : edited.some((row) => row.unit === 'PERCENTAGE' && parseFloat(row.amount) > 100)
        ? 'percentageMax'
        : null

  function apply() {
    const feesOf = (kind: FeeKind) =>
      draft
        .filter((row) => row.kind === kind)
        .map((row) => feeOf(row, currency))
        .filter((fee): fee is Fee => !!fee)
    onSave({
      cpmFees: feesOf('CPM'),
      supplementaryFees: canEditSupplementary ? feesOf('SUPPLEMENTARY') : supplementaryFees,
    })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('fees.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('fees.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-auto text-sm text-muted-foreground">
              {t('fees.feeCount', { count: draft.length })}
            </span>
            {canAddSupplementary && (
              <Button type="button" variant="outline" size="sm" onClick={() => add('SUPPLEMENTARY')}>
                <Plus className="size-4" />
                {t('fees.newFeeOf', { label: supplementaryLabel })}
              </Button>
            )}
            <Button type="button" variant="outline" size="sm" onClick={() => add('CPM')}>
              <Plus className="size-4" />
              {t('fees.newCpmFee')}
            </Button>
          </div>

          {draft.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('fees.noFees')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('fees.name')}</TableHead>
                  <TableHead>{t('fees.feeType')}</TableHead>
                  <TableHead className="w-40">{t('fees.amount')}</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="align-top">
                      <Input
                        value={row.name}
                        aria-label={t('fees.name')}
                        aria-invalid={!row.untouched && !row.name.trim()}
                        autoFocus={row.added}
                        onChange={(e) => update(row.key, { name: e.target.value })}
                      />
                      {row.kind === 'SUPPLEMENTARY' && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-2">
                          <Badge variant="blue">{supplementaryLabel}</Badge>
                          <span className="text-xs text-muted-foreground">
                            {t('fees.adOpsOnly')}
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      {/* Tabs as a two-way switch, like the include/exclude
                          toggle: the choice itself is what the tabs stand for. */}
                      <Tabs
                        value={row.unit}
                        onValueChange={(v) => update(row.key, { unit: v as FeeUnit })}
                      >
                        <TabsList aria-label={t('fees.feeType')}>
                          <TabsTrigger value="MONETARY">{t('fees.unitMonetary')}</TabsTrigger>
                          <TabsTrigger value="PERCENTAGE">{t('fees.unitPercentage')}</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </TableCell>
                    <TableCell className="align-top">
                      <AmountInput
                        value={row.amount}
                        placeholder=""
                        max={row.unit === 'PERCENTAGE' ? 100 : undefined}
                        unit={row.unit === 'PERCENTAGE' ? '%' : currency}
                        onChange={(v) => update(row.key, { amount: v })}
                      />
                    </TableCell>
                    <TableCell className="align-top">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t('fees.removeFee')}
                        onClick={() => remove(row.key)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {readOnlyFees.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                {t('fees.otherFees')}
              </span>
              <Table>
                <TableBody>
                  {readOnlyFees.map((fee, i) => (
                    <TableRow key={`${fee.source}-${i}`}>
                      <TableCell className="pl-0 text-sm">{feeLabel(fee)}</TableCell>
                      <TableCell className="pr-0 text-right text-sm tabular-nums">
                        {feeAmountText(fee)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {grossCpm && (
            <div className="flex flex-col gap-1 rounded-md border p-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('fees.grossCpm')}</span>
                <span className="tabular-nums">{formatCurrencyAmount(grossCpm)}</span>
              </div>
              <div className="flex justify-between font-semibold">
                <span>{t('fees.netCpm')}</span>
                <span className={cn('tabular-nums', feesExceedGross && 'text-(--ad-badge-red)')}>
                  {netCpm
                    ? formatMoneyRange(netCpm.amount, netCpm.maxAmount, netCpm.currency)
                    : '—'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{t('fees.netCpmHelp')}</p>
              {feesExceedGross && (
                <p className="text-xs text-(--ad-badge-red)">{t('fees.feesExceedGross')}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="sm:items-center">
          {blockedBy && (
            <p className="mr-auto text-sm text-(--ad-text-muted)">{t(`fees.${blockedBy}`)}</p>
          )}
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={!!blockedBy} onClick={apply}>
            {t('common.apply')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
